import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'TemplateDesigner/previewUrls';

/**
 * Response from the TemplateDesigner preview URLs endpoint.
 */
export interface PreviewUrlsResponse {
	/** URL for meeting preview. */
	meetingUrl: string | null;
	/** URL for survey preview. */
	surveyUrl: string | null;
	/** Whether a meeting URL is available. */
	meetingAvailable: boolean;
	/** Whether a survey URL is available. */
	surveyAvailable: boolean;
}

/**
 * Query hook to fetch preview URLs for the Template Designer.
 * Returns meeting and survey URLs for preview tabs.
 */
export const useTemplateDesignerPreviewUrlsQuery = ({
	impersonationContext,
	enabled = true,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const value = await userSession.webServiceHelper.callAsync<PreviewUrlsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'TemplateDesigner',
					impersonationContext,
				}),
				'GET'
			);
			return value;
		},
		queryKey: [BASE_QUERY_KEY, impersonationContext],
		staleTime: 60000, // Cache for 1 minute
	});
};

export const invalidateTemplateDesignerPreviewUrlsQuery = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
